import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, EchecApi } from '../partage/api/client';
import type { ReponseBoite } from '../partage/api/types';
import { cles, relireApres } from '../partage/relectures';
import { boitePasEcrite } from '../partage/vocabulaire';
import { avecLaNote, compteDeLaBarre, sansLaNote, texteANoter } from './reglesDeLaBoite';

/**
 * La lecture de la boîte, la même pour la page et le compte de la barre de gauche. Elle repart au
 * serveur à chaque ouverture de la page (`staleTime: 0`) : une note posée par Claude entre-temps
 * s'y voit.
 */
const LECTURE_DE_LA_BOITE = { queryKey: cles.boite, queryFn: api.boite, staleTime: 0 } as const;

/** Le nombre de notes à côté de « Boîte d'entrée », dans la barre ; rien quand elle est vide. */
export function useCompteDeLaBoite(): number | null {
  return compteDeLaBarre(useQuery(LECTURE_DE_LA_BOITE).data);
}

/**
 * La boîte et ses gestes (chantier boite-d-entree, ticket 02), sur le modèle du profil
 * (`profil/useProfil.ts`) : la réponse du serveur est posée dans la lecture, la page et la barre
 * se mettent à jour sans recharger ; un refus relit la boîte et le dit.
 */
export function useBoite() {
  const client = useQueryClient();
  const cle = cles.boite;
  const lecture = useQuery(LECTURE_DE_LA_BOITE);
  const [annonce, setAnnonce] = useState<string | null>(null);

  const dire = useCallback((erreur: unknown) => {
    setAnnonce(boitePasEcrite(erreur instanceof EchecApi ? erreur.code : 'erreur'));
  }, []);

  const changer = useCallback(
    async (changement: (avant: ReponseBoite) => ReponseBoite) => {
      await client.cancelQueries({ queryKey: cle });
      client.setQueryData<ReponseBoite>(cle, (avant) => (avant ? changement(avant) : avant));
    },
    [client, cle]
  );

  /** Noter : la note arrive en haut quand le serveur l'a. La promesse dit si elle est notée. */
  const noter = useCallback(
    async (ecrit: string): Promise<boolean> => {
      const texte = texteANoter(ecrit);
      if (!texte) return false;
      try {
        const { note } = await api.noterDansLaBoite(texte);
        setAnnonce(null);
        await changer((l) => avecLaNote(l, note));
        return true;
      } catch (erreur) {
        dire(erreur);
        return false;
      }
    },
    [changer, dire]
  );

  /** Retirer : la note part tout de suite ; refusé, la boîte est relue. */
  const retirer = useCallback(
    (id: string) => {
      void changer((l) => sansLaNote(l, id));
      api.retirerUneNote(id).then(
        () => setAnnonce(null),
        (erreur: unknown) => {
          void client.invalidateQueries({ queryKey: cle });
          dire(erreur);
        }
      );
    },
    [changer, client, cle, dire]
  );

  /**
   * Ranger : la note devient une bulle dans la mère choisie, puis quitte la boîte ; l'arbre est
   * relu comme après une création (la bulle se voit dans son projet). Refusé, la boîte est relue.
   */
  const ranger = useCallback(
    async (id: string, mereId: string): Promise<void> => {
      try {
        await api.rangerUneNote(id, mereId);
        setAnnonce(null);
        await changer((l) => sansLaNote(l, id));
        void relireApres(client, 'creer');
      } catch (erreur) {
        void client.invalidateQueries({ queryKey: cle });
        dire(erreur);
      }
    },
    [changer, client, cle, dire]
  );

  return { notes: lecture.data?.notes ?? null, noter, retirer, ranger, annonce };
}
